<script setup lang="ts">
import { Category } from '@/models/category.model';
import { User } from '@/models/user.model';
import { ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { computed, onMounted, PropType } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true,
  },
});

const store = useStore<
  ShiftCMSState & { us: UsersState; cs: CategoriesState }
>();

const emit = defineEmits(['reset']);

const categories = computed<Category[]>(() => store.state.cs.categories);
const interests = computed<Category[]>(() => store.state.us.interests);

function resetBPT() {
  if (confirm(`Are you sure you want to reset this user's BPT information`)) {
    store.dispatch(UsersActions.ResetBPT, props.user);

    emit('reset');
  }
}

onMounted(() => {
  store.dispatch(CategoriesActions.GetCategories);
});
</script>
<template>
  <v-card class="mt-4" v-if="user.bptInfo">
    <v-card-title
      >Behaviour and Profile Tracker
      <v-btn color="warning" @click="resetBPT">Reset</v-btn></v-card-title
    >

    <v-card-text v-if="interests && interests.length">
      <h4>Interests</h4>
      <ul class="ml-16">
        <li v-for="interest in interests" :key="interest.id">
          {{ interest.title }}
        </li>
      </ul>
    </v-card-text>
    <v-card-text v-if="user.bptInfo.expertise.length">
      <h4>Expertise</h4>
      <ul class="ml-16">
        <li
          v-for="expertise in user.bptInfo.expertise"
          :key="expertise.categoryId"
        >
          {{ categories.find((c) => c.id === expertise.categoryId)?.title }}
          <v-chip density="compact">{{ expertise.level }}</v-chip>
        </li>
      </ul>
    </v-card-text>
    <v-card-text v-if="user.bptInfo.motivation.length">
      <h4>Motivation</h4>
      <ul class="ml-16">
        <li v-for="mot in user.bptInfo.motivation" :key="mot.option">
          <v-chip density="compact">{{ mot.option }}</v-chip>
          {{ mot.openAnswer }}
        </li>
      </ul>
    </v-card-text>
    <v-card-text v-if="user.bptInfo.behavior.length">
      <h4>Behavior</h4>
      <ul class="ml-16">
        <li v-for="beh in user.bptInfo.behavior" :key="beh.option">
          <v-chip density="compact">{{ beh.option }}</v-chip>
          {{ beh.openAnswer }}
        </li>
      </ul>
    </v-card-text>
    <v-card-text v-if="user.bptInfo.barriers.length">
      <h4>Barriers</h4>
      <ul class="ml-16">
        <li v-for="bar in user.bptInfo.barriers" :key="bar.option">
          <v-chip density="compact">{{ bar.option }}</v-chip>
          {{ bar.openAnswer }}
        </li>
      </ul>
    </v-card-text>

    <v-card-text>
      <p
        v-if="
          !(
            user.bptInfo.expertise.length &&
            user.bptInfo.motivation.length &&
            user.bptInfo.behavior.length &&
            user.bptInfo.barriers.length
          )
        "
      >
        User has not filled out any BPT information
      </p>
    </v-card-text>
  </v-card>
</template>
